import { ElementRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { BarcodeReader, CodeDetection, Configuration, SdkError, StrichSDK } from "@pixelverse/strichjs-sdk";
import { BehaviorSubject, Subject } from "rxjs";
import { ToastrService } from 'ngx-toastr';

import { SecurityService } from 'app/core/security/security.service';
import { EncryptionService } from 'app/services/encryption.service';
import { StrichService } from 'app/shared/services/strich.service';

@Injectable({ providedIn: 'root' })
export class MobileScanningService {

    sec = inject(SecurityService);
    strichService = inject(StrichService);
    encryption = inject(EncryptionService);
    toastrService = inject(ToastrService);
    private ngZone = inject(NgZone);

    sdkInitialized = new BehaviorSubject<boolean>(false);

    sdkInitializationError?: SdkError;
    barcodeReader?: BarcodeReader;
    detected = new Subject<CodeDetection[]>();
    pendingInitialization = false;
    pendingDestroy = false;
    useAudio = signal<boolean>(false);
    isPid = signal<boolean>(false); 

    errorMessage : string;

    constructor() {        
        this.strichService.initializeStritch();
    }

    async newBarcodeReader(hostElement: ElementRef): Promise<BarcodeReader> {
        if (this.barcodeReader) { 
            //throw new Error(`A BarcodeReader already exists. Be sure to release it before switching routes.`);
            await this.releaseBarcodeReader();
        }

        try {
            this.pendingInitialization = true;
            this.barcodeReader = await new BarcodeReader(this.getConfiguration(hostElement)).initialize();
            this.barcodeReader.detected = (detections) => this.detected.next(detections);
            await this.barcodeReader.start();
            return this.barcodeReader;
        } finally { this.pendingInitialization = false; }
    }

    async releaseBarcodeReader(): Promise<void> {
        if (this.barcodeReader) {
            this.pendingDestroy = true;
            try {
                await this.barcodeReader.destroy();
                this.barcodeReader = undefined;
            } catch (err) { console.error(`Failed to destroy() BarcodeReader, ignoring`, err); } 
            finally { this.pendingDestroy = false; } // ignore exception in destroy()
        }
    }

    async stopScanning(): Promise<void> { if (this.barcodeReader) { await this.barcodeReader.stop(); } }
    async startScanning(): Promise<void> { if (this.barcodeReader) { await this.barcodeReader.start(); } }

    private getConfiguration(hostElement: ElementRef): Configuration {
        const basePath = document.getElementsByTagName('base')[0].href;
        return {
            selector: hostElement.nativeElement
            , engine: {
                symbologies: ['code128', 'datamatrix', 'qr', 'pdf417'] // add more symbologies (barcode types) as needed
                , minScanlinesNeeded: this.isPid() ? 1 : 3
                , invertedCodes: true
                , duplicateInterval: 2000 // increases time (ms) between scans of the same barcode, reduces duplicate detections
            }
            , frameSource: { 
                resolution: 'full-hd' // full-hd is recommended for more challenging codes  
                , rememberCameraDeviceId: true
            } 
            , overlay: {
                customLogoSrc: basePath + "assets/images/logos/logo_small.png"
                , showCameraSelector: false
                , showDetections: true
                , showFlashlight: true
                , showTargetingLine: true
                , primaryColor: 'rgb(236,237,237)'
                , maskColor: 'rgba(0,0,0,0.5)'
                , viewfinderBorderWidth: 5
                , viewfinderCornerRadius: 20
            }
            , locator: {                
                regionOfInterest: { // restrict active area to a horizontal bar in the center
                    left: 0.10
                    , right: 0.10
                    , top: 0.30
                    , bottom: 0.30
                }
            }
            , feedback: { // get audio feedback for scans               
                audio: this.useAudio()
                , vibration: false
            }
        };
    }
}
